import "./pulse.css"

export const PULSE_CLASS = "agent-pulse"

/**
 * Must outlive the `.agent-pulse` animation in pulse.css (1.4s x 3 = 4200ms),
 * because this is the timer that strips the class: set shorter and the last
 * pulse is cut off mid-expansion, which is what the old 2000ms did against a
 * 2800ms animation. The extra 200ms is slack, not a second animation cycle.
 */
const DEFAULT_DURATION_MS = 4400

/**
 * Flash a single element: scroll it into view and apply the pulse ring/glow
 * class for `durationMs`, then auto-clear.
 *
 * Returns a cleanup function that removes the class and cancels the timer
 * immediately (e.g. if a newer pulse supersedes this one).
 */
export function pulseElement(
  el: HTMLElement,
  durationMs = DEFAULT_DURATION_MS,
): () => void {
  el.scrollIntoView({ behavior: "smooth", block: "center" })
  el.classList.add(PULSE_CLASS)

  const timer = setTimeout(() => {
    el.classList.remove(PULSE_CLASS)
  }, durationMs)

  return () => {
    clearTimeout(timer)
    el.classList.remove(PULSE_CLASS)
  }
}

/**
 * Resolve a selector and pulse the first match. Returns `false` if nothing
 * matched, so callers can fall back to a graceful message.
 */
export function pulseTarget(
  selector: string,
  durationMs = DEFAULT_DURATION_MS,
): boolean {
  const el = document.querySelector<HTMLElement>(selector)
  if (!el) {
    return false
  }
  pulseElement(el, durationMs)
  return true
}
